import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, ArrowLeft, LogOut, Loader2, Lock, Plus, Copy, Check, Trash2, Power, RefreshCw, AlertCircle, Key,
  PlayCircle, X as XIcon,
} from 'lucide-react';
import api from '../utils/api';

/**
 * AdminApiKeys.js — Admin-only management for the Agent-to-Agent API key model.
 *
 * Mounted at /admin-portal-7x9k2m/api-keys. Gated behind the
 * `adminApiKeysPanel` feature flag — if OFF the page shows a notice and nothing else.
 *
 * On this page admins can:
 *   - List all API keys (key body redacted to last 8 chars)
 *   - Create a new key (plaintext shown ONCE on creation)
 *   - Top up credits
 *   - Enable / disable keys
 *   - Edit label, wallet address, webhook, notes
 *   - Delete keys (rare — usually disable instead)
 *
 * Note: this is the management UI only. The user-facing /api/agent/* endpoints
 * are gated independently behind the `agentApiEnabled` feature flag and
 * mounted in backend/routes/agentApi.js (still to build — Item 5).
 */

const AdminApiKeys = () => {
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState({ label: '', credits: 0, walletAddress: '', webhookUrl: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // holds { key, label } after creation
  const [copied, setCopied] = useState(false);

  // Top-up
  const [topupId, setTopupId] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');

  // Dry-run (simulate a public /api/agent/reports/create call)
  const [dryRunId, setDryRunId] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const features = await api.admin.getFeatures();
        const enabled = features?.adminApiKeysPanel === true;
        setFeatureEnabled(enabled);
        if (enabled) await loadKeys();
      } catch (err) {
        console.error('Failed to fetch features:', err.message);
        setFeatureEnabled(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadKeys = async () => {
    try {
      const data = await api.admin.listApiKeys();
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load API keys' });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKey.label.trim()) {
      setMessage({ type: 'error', text: 'Label is required' });
      return;
    }
    setCreating(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await api.admin.createApiKey({
        label: newKey.label.trim(),
        credits: Number(newKey.credits) || 0,
        walletAddress: newKey.walletAddress.trim(),
        webhookUrl: newKey.webhookUrl.trim(),
        notes: newKey.notes.trim(),
      });
      setCreatedKey({ key: result.key, label: result.label });
      setNewKey({ label: '', credits: 0, walletAddress: '', webhookUrl: '', notes: '' });
      setShowCreate(false);
      await loadKeys();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to create API key' });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* ignore */ }
  };

  const handleToggleEnabled = async (key) => {
    try {
      await api.admin.updateApiKey(key._id, { enabled: !key.enabled });
      await loadKeys();
      setMessage({ type: 'success', text: `Key ${key.enabled ? 'disabled' : 'enabled'}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleTopup = async (id) => {
    const amt = Number(topupAmount);
    if (!amt || amt <= 0) {
      setMessage({ type: 'error', text: 'Enter a positive credit amount' });
      return;
    }
    try {
      await api.admin.topupApiKey(id, amt);
      setTopupId(null);
      setTopupAmount('');
      await loadKeys();
      setMessage({ type: 'success', text: `Added ${amt} credits` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleDryRun = async (key) => {
    // Toggle: clicking the same row's button again closes the panel
    if (dryRunId === key._id) {
      setDryRunId(null);
      setDryRunResult(null);
      return;
    }
    setDryRunId(key._id);
    setDryRunResult(null);
    setDryRunLoading(true);
    try {
      const result = await api.admin.dryRunApiKey(key._id, 1);
      setDryRunResult(result);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Dry-run failed' });
      setDryRunId(null);
    } finally {
      setDryRunLoading(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Permanently delete API key "${key.label}"? Prefer disabling instead so the audit trail is preserved.`)) return;
    try {
      await api.admin.deleteApiKey(key._id);
      await loadKeys();
      setMessage({ type: 'success', text: 'API key deleted' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg"><Shield className="h-6 w-6 text-white" /></div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Agent API Keys</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white"><ArrowLeft className="h-4 w-4" />User Portal</Link>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white"><LogOut className="h-4 w-4" />Sign Out</button>
          </div>
        </div>
      </header>

      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 flex-wrap">
            <Link to="/admin-portal-7x9k2m/dashboard" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Dashboard</Link>
            <Link to="/admin-portal-7x9k2m/reports" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Reports</Link>
            <Link to="/admin-portal-7x9k2m/users" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Users</Link>
            <Link to="/admin-portal-7x9k2m/config" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Configuration</Link>
            <Link to="/admin-portal-7x9k2m/vouchers" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Vouchers</Link>
            <Link to="/admin-portal-7x9k2m/reviews" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Reviews</Link>
            <Link to="/admin-portal-7x9k2m/audit" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">Audit</Link>
            <Link to="/admin-portal-7x9k2m/api-keys" className="px-4 py-3 text-sm font-medium text-white border-b-2 border-red-500">API Keys</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading && (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-red-500 animate-spin" /></div>
        )}

        {!loading && featureEnabled === false && (
          <div className="bg-gray-800 rounded-lg border border-amber-700/40 p-8 text-center">
            <Lock className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-white mb-2">API Keys panel disabled</h2>
            <p className="text-sm text-gray-400 max-w-md mx-auto">
              The <code className="text-amber-300">adminApiKeysPanel</code> feature flag is OFF.
              Enable it in <Link to="/admin-portal-7x9k2m/config" className="text-red-400 hover:text-red-300 underline">Configuration → Feature Flags</Link>.
            </p>
            <p className="text-xs text-gray-500 mt-4">
              Note: enabling this panel does NOT expose any user-facing API endpoints.
              It only lets admins create + manage agent API keys for testing.
              The public <code>/api/agent/*</code> routes are separately gated by <code className="text-amber-300">agentApiEnabled</code>.
            </p>
          </div>
        )}

        {!loading && featureEnabled === true && (
          <>
            {message.text && (
              <div className={`mb-4 p-3 rounded-md text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-green-900/40 border border-green-700/50 text-green-200' : 'bg-red-900/40 border border-red-700/50 text-red-200'}`}>
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {message.text}
              </div>
            )}

            {/* One-time plaintext key reveal */}
            {createdKey && (
              <div className="mb-6 p-4 rounded-lg bg-amber-900/30 border border-amber-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="h-5 w-5 text-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-200">Save this key now — it will not be shown again</h3>
                </div>
                <p className="text-xs text-amber-200/80 mb-2">Label: <strong>{createdKey.label}</strong></p>
                <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded p-3">
                  <code className="flex-1 text-sm text-amber-200 font-mono break-all">{createdKey.key}</code>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs flex-shrink-0"
                  >
                    {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                  </button>
                </div>
                <button
                  onClick={() => setCreatedKey(null)}
                  className="mt-3 text-xs text-amber-300 hover:text-amber-200 underline"
                >
                  I've saved it — dismiss
                </button>
              </div>
            )}

            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-xl font-bold text-white">Agent API Keys</h1>
                <p className="text-sm text-gray-400 mt-1">Pre-paid keys for the Agent-to-Agent API. {keys.length} key{keys.length === 1 ? '' : 's'} registered.</p>
              </div>
              <button
                onClick={() => setShowCreate(!showCreate)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm"
              >
                <Plus className="h-4 w-4" /> New API Key
              </button>
            </div>

            {showCreate && (
              <form onSubmit={handleCreate} className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6 space-y-4">
                <h3 className="text-base font-semibold text-white">Create new API key</h3>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Label *</label>
                  <input
                    type="text"
                    value={newKey.label}
                    onChange={e => setNewKey({ ...newKey, label: e.target.value })}
                    placeholder="e.g. VC Fund Agent — GPT-5 workflow"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white"
                    maxLength={100}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Initial credits</label>
                    <input
                      type="number"
                      min="0"
                      value={newKey.credits}
                      onChange={e => setNewKey({ ...newKey, credits: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Solana wallet (optional)</label>
                    <input
                      type="text"
                      value={newKey.walletAddress}
                      onChange={e => setNewKey({ ...newKey, walletAddress: e.target.value })}
                      placeholder="For future USDC top-ups"
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white font-mono"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Webhook URL (optional)</label>
                  <input
                    type="url"
                    value={newKey.webhookUrl}
                    onChange={e => setNewKey({ ...newKey, webhookUrl: e.target.value })}
                    placeholder="https://example.com/id100/report-complete"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Admin notes</label>
                  <textarea
                    value={newKey.notes}
                    onChange={e => setNewKey({ ...newKey, notes: e.target.value })}
                    rows={2}
                    maxLength={500}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-sm text-white"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
                  <button type="submit" disabled={creating} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md text-sm">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Create
                  </button>
                </div>
              </form>
            )}

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              {keys.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                  No API keys yet. Click <strong>New API Key</strong> to create your first one.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/40 text-xs text-gray-400 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Label</th>
                      <th className="text-left px-4 py-3">Key</th>
                      <th className="text-right px-4 py-3">Credits</th>
                      <th className="text-right px-4 py-3">Used</th>
                      <th className="text-left px-4 py-3">Last used</th>
                      <th className="text-center px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map(k => (
                      <React.Fragment key={k._id}>
                        <tr className="border-t border-gray-700">
                          <td className="px-4 py-3 text-white">
                            <div>{k.label}</div>
                            {k.notes && <div className="text-xs text-gray-500 mt-0.5 italic">{k.notes}</div>}
                            {k.walletAddress && <div className="text-xs text-gray-500 mt-0.5 font-mono">{k.walletAddress}</div>}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-300">{k.keyPreview}</td>
                          <td className="px-4 py-3 text-right text-white font-medium">{k.credits}</td>
                          <td className="px-4 py-3 text-right text-gray-300">{k.totalReportsRun}</td>
                          <td className="px-4 py-3 text-xs text-gray-400">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded ${k.enabled ? 'bg-green-900/50 text-green-300' : 'bg-gray-700 text-gray-400'}`}>
                              {k.enabled ? 'enabled' : 'disabled'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 justify-end">
                              <button
                                onClick={() => { setTopupId(topupId === k._id ? null : k._id); setTopupAmount(''); }}
                                title="Top up credits"
                                className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDryRun(k)}
                                title="Dry-run: simulate a public /api/agent request with this key"
                                className={`p-1.5 hover:bg-gray-700 rounded ${dryRunId === k._id ? 'text-blue-300' : 'text-blue-400'}`}
                              >
                                <PlayCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleEnabled(k)}
                                title={k.enabled ? 'Disable' : 'Enable'}
                                className={`p-1.5 hover:bg-gray-700 rounded ${k.enabled ? 'text-green-400' : 'text-gray-500'}`}
                              >
                                <Power className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(k)}
                                title="Delete (use sparingly)"
                                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {topupId === k._id && (
                          <tr className="border-t border-gray-700 bg-gray-900/40">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">Add credits:</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={topupAmount}
                                  onChange={e => setTopupAmount(e.target.value)}
                                  placeholder="e.g. 10"
                                  className="w-32 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-white"
                                />
                                <button
                                  onClick={() => handleTopup(k._id)}
                                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => { setTopupId(null); setTopupAmount(''); }}
                                  className="px-3 py-1 text-gray-400 hover:text-white text-xs"
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        {dryRunId === k._id && (
                          <tr className="border-t border-gray-700 bg-gray-900/40">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                                    <PlayCircle className="h-4 w-4 text-blue-400" />
                                    Dry-run simulation
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">
                                    No real report runs. This shows what the public <code>/api/agent/reports/create</code> route
                                    would do if a request arrived right now with this key.
                                  </p>
                                </div>
                                <button
                                  onClick={() => { setDryRunId(null); setDryRunResult(null); }}
                                  className="p-1 text-gray-400 hover:text-white"
                                >
                                  <XIcon className="h-4 w-4" />
                                </button>
                              </div>

                              {dryRunLoading && (
                                <div className="flex items-center gap-2 text-sm text-gray-400">
                                  <Loader2 className="h-4 w-4 animate-spin" /> Running simulation…
                                </div>
                              )}

                              {!dryRunLoading && dryRunResult && (
                                <div className="space-y-3">
                                  <div className={`p-3 rounded text-sm ${dryRunResult.wouldSucceed ? 'bg-green-900/30 border border-green-700/40 text-green-200' : 'bg-red-900/30 border border-red-700/40 text-red-200'}`}>
                                    <div className="flex items-center gap-2 font-medium">
                                      {dryRunResult.wouldSucceed ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                                      {dryRunResult.message}
                                    </div>
                                    {!dryRunResult.wouldSucceed && dryRunResult.errors?.length > 0 && (
                                      <ul className="mt-2 ml-5 list-disc text-xs space-y-0.5">
                                        {dryRunResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                      </ul>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {dryRunResult.checks?.map((c, i) => (
                                      <div key={i} className="flex items-start gap-2 p-2 rounded bg-gray-800/60 border border-gray-700">
                                        {c.pass
                                          ? <Check className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                                          : <XIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />}
                                        <div className="text-xs">
                                          <div className={`font-medium ${c.pass ? 'text-green-300' : 'text-red-300'}`}>{c.name}</div>
                                          <div className="text-gray-400 mt-0.5">{c.detail}</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  <div className="text-xs text-gray-400 bg-gray-800/40 border border-gray-700 rounded p-2 grid grid-cols-3 gap-2">
                                    <div>
                                      <div className="text-gray-500 uppercase tracking-wide">Credits before</div>
                                      <div className="text-white">{dryRunResult.simulated?.creditsBefore}</div>
                                    </div>
                                    <div>
                                      <div className="text-gray-500 uppercase tracking-wide">Would deduct</div>
                                      <div className="text-white">{dryRunResult.simulated?.wouldDeduct}</div>
                                    </div>
                                    <div>
                                      <div className="text-gray-500 uppercase tracking-wide">Credits after</div>
                                      <div className="text-white">{dryRunResult.simulated?.creditsAfter}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="text-xs text-gray-500 mt-4 italic">
              The full plaintext key is shown only once on creation. After that only the last 8 chars are visible.
              Keys can be disabled (preserves audit history) or deleted (rare).
              The user-facing <code>/api/agent/*</code> endpoints are separately gated behind <code>agentApiEnabled</code>.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminApiKeys;
